import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useFarm } from '../../contexts/FarmContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  ChevronDownIcon,
  CubeIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  HeartIcon,
  BuildingStorefrontIcon,
  WalletIcon,
  SparklesIcon,
  ShieldCheckIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const Sidebar = ({ onNavigate, onClose }) => {
  const { farms, activeFarms, currentFarm, loading } = useFarm();
  const { user } = useAuth();
  const location = useLocation();
  const [marketplaceOpen, setMarketplaceOpen] = useState(
    location.pathname.startsWith('/shop') || location.pathname.startsWith('/market')
  );

  const accountType = user?.accountType || 'farmer';

  // Theme colors based on account type
  const themeColors = {
    farmer: {
      primary: 'green',
      gradient: 'from-green-600 to-emerald-600',
      bg: 'bg-green-600',
      bgHover: 'hover:bg-green-700',
      bgLight: 'bg-green-50',
      bgLightHover: 'hover:bg-green-100',
      text: 'text-green-700',
      textLight: 'text-green-600',
      border: 'border-green-600',
      ring: 'ring-green-500',
      badge: 'bg-green-100 text-green-800',
      activeBg: 'bg-green-50',
      activeText: 'text-green-700',
      activeBorder: 'border-green-500',
      iconActive: 'text-green-600'
    },
    supplier: {
      primary: 'blue',
      gradient: 'from-blue-600 to-indigo-600',
      bg: 'bg-blue-600',
      bgHover: 'hover:bg-blue-700',
      bgLight: 'bg-blue-50',
      bgLightHover: 'hover:bg-blue-100',
      text: 'text-blue-700',
      textLight: 'text-blue-600',
      border: 'border-blue-600',
      ring: 'ring-blue-500',
      badge: 'bg-blue-100 text-blue-800',
      activeBg: 'bg-blue-50',
      activeText: 'text-blue-700',
      activeBorder: 'border-blue-500',
      iconActive: 'text-blue-600'
    },
    client: {
      primary: 'amber',
      gradient: 'from-amber-500 to-orange-500',
      bg: 'bg-amber-500',
      bgHover: 'hover:bg-amber-600',
      bgLight: 'bg-amber-50',
      bgLightHover: 'hover:bg-amber-100',
      text: 'text-amber-700',
      textLight: 'text-amber-600',
      border: 'border-amber-500',
      ring: 'ring-amber-500',
      badge: 'bg-amber-100 text-amber-800',
      activeBg: 'bg-amber-50',
      activeText: 'text-amber-700',
      activeBorder: 'border-amber-500',
      iconActive: 'text-amber-600'
    }
  };

  const theme = themeColors[accountType] || themeColors.farmer;

  // Farmer navigation
  const farmerNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Farms', href: '/farms', icon: BuildingOfficeIcon },
    { name: 'Activities', href: '/activities', icon: CalendarDaysIcon },
    { name: 'Hatching', href: '/hatching', icon: SparklesIcon },
    { name: 'Records', href: '/records', icon: ChartBarIcon },
    { name: 'AI Assistant', href: '/ai-assistant', icon: ChatBubbleLeftRightIcon },
    { name: 'Wallet', href: '/wallet', icon: WalletIcon },
  ];

  // Supplier navigation
  const supplierNavigation = [
    { name: 'Dashboard', href: '/supplier/dashboard', icon: HomeIcon },
    { name: 'Products', href: '/supplier/products', icon: CubeIcon },
    { name: 'Orders', href: '/supplier/orders', icon: ClipboardDocumentListIcon },
    { name: 'Wallet', href: '/wallet', icon: WalletIcon },
    { name: 'Settings', href: '/supplier/settings', icon: Cog6ToothIcon },
  ];

  // Client navigation
  const clientNavigation = [
    { name: 'Dashboard', href: '/client/dashboard', icon: HomeIcon },
    { name: 'Explore Farms', href: '/client/explore', icon: MagnifyingGlassIcon },
    { name: 'My Orders', href: '/client/orders', icon: ClipboardDocumentListIcon },
    { name: 'Wallet', href: '/wallet', icon: WalletIcon },
    { name: 'Favorites', href: '/client/favorites', icon: HeartIcon },
    { name: 'Settings', href: '/client/settings', icon: Cog6ToothIcon },
  ];

  const navigation = accountType === 'supplier' 
    ? supplierNavigation 
    : accountType === 'client' 
      ? clientNavigation 
      : farmerNavigation;

  const marketplaceItems = [
    { name: 'Sell Products', href: '/shop', icon: ShoppingBagIcon },
    { name: 'Buy Products', href: '/market', icon: ShoppingCartIcon },
  ];

  const isMarketplaceActive = location.pathname.startsWith('/shop') || location.pathname.startsWith('/market');

  const isActive = (href) => {
    if (href === '/dashboard' || href === '/supplier/dashboard' || href === '/client/dashboard') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    return (
      <NavLink
        key={item.name}
        to={item.href}
        onClick={() => { onNavigate && onNavigate(item.href); onClose && onClose(); }}
        className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
          active
            ? `${theme.activeBg} ${theme.activeText} shadow-sm`
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <div className={`p-1.5 rounded-lg mr-3 transition-colors ${
          active 
            ? `bg-gradient-to-br ${theme.gradient} text-white shadow-sm` 
            : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
        }`}>
          <Icon className="h-4 w-4" />
        </div>
        {item.name}
        {active && (
          <div className={`ml-auto w-1.5 h-1.5 rounded-full bg-gradient-to-br ${theme.gradient}`}></div>
        )}
      </NavLink>
    );
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-screen overflow-y-auto flex flex-col">
      {/* Header with gradient */}
      <div className={`p-5 bg-gradient-to-br ${theme.gradient} text-white`}>
        <div className="flex items-center gap-3">
          {accountType === 'supplier' && user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.businessName || 'Business logo'}
              className="w-10 h-10 rounded-xl object-cover bg-white/20"
            />
          ) : (
            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              {accountType === 'supplier' && <BuildingStorefrontIcon className="h-5 w-5" />}
              {accountType === 'client' && <ShoppingCartIcon className="h-5 w-5" />}
              {accountType === 'farmer' && <BuildingOfficeIcon className="h-5 w-5" />}
            </div>
          )}
          <div>
            <p className="text-white/80 text-xs">Welcome back</p>
            <p className="font-semibold truncate">
              {accountType === 'supplier'
                ? (user?.businessName || user?.firstName || 'Supplier')
                : (user?.firstName || 'User')}
            </p>
          </div>
        </div>
        <div className="mt-3">
          <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-white/20 backdrop-blur text-xs font-medium">
            {accountType === 'supplier' && <BuildingStorefrontIcon className="h-3 w-3 mr-1" />}
            {accountType === 'client' && <ShoppingCartIcon className="h-3 w-3 mr-1" />}
            {accountType === 'farmer' && <BuildingOfficeIcon className="h-3 w-3 mr-1" />}
            {accountType.charAt(0).toUpperCase() + accountType.slice(1)} Account
          </span>
        </div>
      </div>

      <div className="p-4 flex-1 overflow-y-auto">
        {/* Farm Selection - Only for Farmers */}
        {accountType === 'farmer' && (
          <>
            {loading && farms.length === 0 ? (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Current Farm
                </label>
                <div className="w-full h-10 bg-gray-100 rounded-xl animate-pulse"></div>
              </div>
            ) : (activeFarms || []).length > 0 ? (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Current Farm
                </label>
                <select 
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  value={currentFarm?.id || ''}
                  onChange={(e) => {
                    const farm = (activeFarms || []).find(f => f.id === e.target.value);
                    if (farm) {
                      window.location.href = `/farm/${farm.id}`;
                    }
                  }}
                >
                  <option value="">Select a farm</option>
                  {(activeFarms || []).map((farm) => (
                    <option key={farm.id} value={farm.id}>
                      {farm.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {/* Quick Action - New Farm */}
            <div className="mb-4">
              <NavLink
                to="/farm/setup"
                className={`flex items-center justify-center w-full px-4 py-2.5 bg-gradient-to-r ${theme.gradient} text-white rounded-xl hover:shadow-lg hover:shadow-green-500/25 transition-all duration-200 text-sm font-medium`}
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Farm
              </NavLink>
            </div>
          </>
        )}

        {/* Supplier Quick Action */}
        {accountType === 'supplier' && (
          <div className="mb-4">
            <NavLink
              to="/supplier/products"
              className={`flex items-center justify-center w-full px-4 py-2.5 bg-gradient-to-r ${theme.gradient} text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 text-sm font-medium`}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Product
            </NavLink>
          </div>
        )}

        {/* Client Quick Action */}
        {accountType === 'client' && (
          <div className="mb-4">
            <NavLink
              to="/client/explore"
              className={`flex items-center justify-center w-full px-4 py-2.5 bg-gradient-to-r ${theme.gradient} text-white rounded-xl hover:shadow-lg hover:shadow-amber-500/25 transition-all duration-200 text-sm font-medium`}
            >
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              Explore Farms
            </NavLink>
          </div>
        )}

        {/* Section Label */}
        <div className="mb-2">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-3">Menu</p>
        </div>

        {/* Main Navigation */}
        <nav className="space-y-1">
          {navigation.map((item) => (
            <NavItem key={item.name} item={item} />
          ))}
          
          {/* Marketplace Collapsible Menu - Only for Farmers */}
          {accountType === 'farmer' && (
            <div>
              <button
                onClick={() => setMarketplaceOpen(!marketplaceOpen)}
                className={`w-full group flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                  isMarketplaceActive
                    ? `${theme.activeBg} ${theme.activeText} shadow-sm`
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center">
                  <div className={`p-1.5 rounded-lg mr-3 transition-colors ${
                    isMarketplaceActive 
                      ? `bg-gradient-to-br ${theme.gradient} text-white shadow-sm` 
                      : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                  }`}>
                    <ShoppingBagIcon className="h-4 w-4" />
                  </div>
                  Marketplace
                </div>
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform duration-200 ${
                    marketplaceOpen ? 'rotate-180' : ''
                  } ${isMarketplaceActive ? theme.iconActive : 'text-gray-400'}`}
                />
              </button>
              {marketplaceOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-100 pl-2">
                  {marketplaceItems.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        onClick={() => { onNavigate && onNavigate(item.href); onClose && onClose(); }}
                        className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          active
                            ? `${theme.activeBg} ${theme.activeText}`
                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        }`}
                      >
                        <item.icon className={`mr-2 h-4 w-4 ${active ? theme.iconActive : 'text-gray-400'}`} />
                        {item.name}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Academy and Billing - only for farmers and suppliers */}
          {(accountType === 'farmer' || accountType === 'supplier') && (
            <>
              <NavItem item={{ name: 'Academy', href: '/academy', icon: AcademicCapIcon }} />
              <NavItem item={{ name: 'Billing', href: '/billing', icon: CreditCardIcon }} />
            </>
          )}

          {user?.role === 'admin' && (
            <NavItem item={{ name: 'Admin', href: '/admin', icon: ShieldCheckIcon }} />
          )}
        </nav>

        {/* Farm List - Only for Farmers */}
        {accountType === 'farmer' && (activeFarms || []).length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-3 mb-2">Your Farms</p>
            <div className="space-y-1">
              {(activeFarms || []).slice(0, 5).map((farm) => {
                const farmActive = location.pathname === `/farm/${farm.id}`;
                return (
                  <NavLink
                    key={farm.id}
                    to={`/farm/${farm.id}`}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                      farmActive
                        ? `${theme.activeBg} ${theme.activeText}`
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full mr-3 ${farmActive ? `bg-gradient-to-br ${theme.gradient}` : 'bg-gray-300'}`}></div>
                    <span className="truncate">{farm.name}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

        {/* Settings - Only for farmers */}
        {accountType === 'farmer' && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <NavItem item={{ name: 'Settings', href: '/profile', icon: Cog6ToothIcon }} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100 bg-gray-50">
        <div className="text-center">
          <p className="text-xs text-gray-400">Omabra Poultry Assistant</p>
          <p className="text-xs text-gray-300 mt-0.5">v2.0</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
